import { roundTo } from '../chart-shared';
import type { ScatterPlotPoint } from './scatter-plot-chart.model';

// ---------------------------------------------------------------------------
// Linear regression (least squares)
// ---------------------------------------------------------------------------

export interface RegressionResult {
  /** Slope */
  readonly a: number;
  /** Intercept */
  readonly b: number;
  /** Pearson correlation coefficient */
  readonly r: number;
  /** Coefficient of determination */
  readonly rSquared: number;
}

/**
 * Compute least-squares linear regression y = ax + b and Pearson r.
 * Returns null if fewer than 2 points or zero variance in x.
 */
export function computeRegression(points: ScatterPlotPoint[]): RegressionResult | null {
  const n = points.length;
  if (n < 2) {
    return null;
  }

  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;
  let sumY2 = 0;

  for (const p of points) {
    sumX += p.x;
    sumY += p.y;
    sumXY += p.x * p.y;
    sumX2 += p.x * p.x;
    sumY2 += p.y * p.y;
  }

  const denomX = n * sumX2 - sumX * sumX;
  const denomY = n * sumY2 - sumY * sumY;

  if (denomX === 0) {
    return null;
  }

  const a = (n * sumXY - sumX * sumY) / denomX;
  const b = (sumY - a * sumX) / n;

  // Pearson r
  const denomR = Math.sqrt(denomX * denomY);
  const r = denomR === 0 ? 0 : (n * sumXY - sumX * sumY) / denomR;

  return {
    a: roundTo(a, 6),
    b: roundTo(b, 6),
    r: roundTo(r, 6),
    rSquared: roundTo(r * r, 6),
  };
}

/**
 * Predict y for a given x using regression line y = ax + b.
 */
export function predictY(regression: RegressionResult, x: number): number {
  return regression.a * x + regression.b;
}

/**
 * Compute residual line endpoints for each data point.
 * Returns an array of [x, actual_y, predicted_y] tuples.
 */
export function computeResiduals(
  points: ScatterPlotPoint[],
  regression: RegressionResult,
): Array<{ x: number; actual: number; predicted: number }> {
  return points.map((p) => ({
    x: p.x,
    actual: p.y,
    predicted: predictY(regression, p.x),
  }));
}
